import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useBillingDetails } from '../components/dialogs/BillingDetailsDialog';
import { toast } from 'sonner';
import { 
  User, Mail, Building2, Calendar, Shield, 
  CreditCard, Zap, Check, Clock, Settings, Bell, Globe,
  Crown, Star, Rocket, X, ArrowLeft, Save, AlertCircle, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Subscription Plans Data - Matches Landing Page
const PLANS = [
  {
    id: 'free',
    name: 'Free (FOREVER)',
    nameAr: 'مجاني (للأبد)',
    price: 0,
    priceAr: '٠',
    period: 'forever',
    periodAr: 'للأبد',
    description: 'Perfect for getting started',
    descriptionAr: 'مثالي للبدء',
    icon: Star,
    color: 'from-slate-500 to-zinc-600',
    features: [
      { en: '1 GCC country access', ar: 'وصول لدولة خليجية واحدة' },
      { en: 'Unlimited Features', ar: 'ميزات غير محدودة' },
      { en: 'Core intelligence feed', ar: 'موجز المعلومات الأساسي' },
      { en: 'Advanced search & filters', ar: 'بحث وفلاتر متقدمة' },
      { en: 'Personal CRM', ar: 'CRM شخصي' },
      { en: 'Affordable market reports', ar: 'تقارير السوق بأسعار معقولة' }
    ],
    limitations: []
  },
  {
    id: 'standard',
    name: 'Standard',
    nameAr: 'قياسي',
    price: 20,
    yearlyPrice: 17,
    priceAr: '٢٠',
    period: '/month',
    periodAr: '/شهر',
    description: 'Best for growing businesses',
    descriptionAr: 'الأفضل للأعمال النامية',
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    popular: true,
    features: [
      { en: 'All 6 GCC countries', ar: 'جميع دول الخليج الست' },
      { en: 'Notifications & Alerts', ar: 'إشعارات وتنبيهات' },
      { en: 'Excel/CSV exports', ar: 'تصدير Excel/CSV' },
      { en: 'Customizable Feed', ar: 'تخصيص الموجز' },
      { en: 'Create Projects & Companies', ar: 'إنشاء مشاريع وشركات' },
      { en: '1 free report/month', ar: 'تقرير مجاني واحد/شهر' },
      { en: '30% report discount', ar: 'خصم 30% على التقارير' }
    ],
    limitations: []
  },
  {
    id: 'premium',
    name: 'Premium',
    nameAr: 'مميز',
    price: 49,
    yearlyPrice: 40,
    priceAr: '٤٩',
    period: '/month',
    periodAr: '/شهر',
    description: 'For power users',
    descriptionAr: 'للمستخدمين المتقدمين',
    icon: Rocket,
    color: 'from-yellow-500 to-amber-500',
    features: [
      { en: 'Everything in Standard', ar: 'كل ما في القياسي' },
      { en: 'Collaborative CRM', ar: 'CRM تعاوني' },
      { en: 'Create News & Tenders', ar: 'إنشاء أخبار ومناقصات' },
      { en: '2 free reports/month', ar: 'تقريران مجانيان/شهر' },
      { en: '50% report discount', ar: 'خصم 50% على التقارير' }
    ],
    limitations: []
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    nameAr: 'المؤسسات',
    price: 36,
    yearlyPrice: 30,
    priceAr: '٣٦',
    period: '/user/month',
    periodAr: '/مستخدم/شهر',
    description: 'For large organizations',
    descriptionAr: 'للمؤسسات الكبيرة',
    icon: Crown,
    color: 'from-blue-600 to-indigo-600',
    features: [
      { en: 'Everything in Premium', ar: 'كل ما في المميز' },
      { en: 'Premium Company Badge', ar: 'شارة الشركة المميزة' },
      { en: 'White Label reports', ar: 'تقارير بالعلامة البيضاء' },
      { en: 'Min. 5 users', ar: 'حد أدنى 5 مستخدمين' }
    ],
    limitations: []
  }
];

export default function SettingsPage() {
  const { user, token, updateUser } = useAuth();
  const { language, isRTL } = useLanguage();
  const { entitlements } = useEntitlements();
  const { billingDialog, askBillingDetails } = useBillingDetails(); // WO-44
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [emailNotif, setEmailNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({
    email_enabled: true, push_enabled: true, in_app_enabled: true,
    digest_email_enabled: true, digest_time: '09:00', timezone: null, team_alerts_enabled: false,
  });
  const [hasTeam, setHasTeam] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [seatCount, setSeatCount] = useState(5);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  // WO-21 — the company's Enterprise seats, who administers the subscription,
  // and the hand-over control for whoever currently does.
  const [entSeats, setEntSeats] = useState(null);
  const [entTransferTo, setEntTransferTo] = useState('');
  const [entTransferring, setEntTransferring] = useState(false);
  const [entSeatTo, setEntSeatTo] = useState('');
  const [entSeatBusy, setEntSeatBusy] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Company website edit state
  const WEBSITE_URL_REGEX = /^(?:https?:\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;
  const [websiteInput, setWebsiteInput] = useState('');
  const [websiteEditing, setWebsiteEditing] = useState(false);
  const [websiteSaving, setWebsiteSaving] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  // WO-21 — the company-website requirement exists to vet PAYING CUSTOMERS:
  // a Premium company should be a real company with a real site. A MID2BID
  // Admin is not a customer being vetted, so the operator account is exempt.
  // Without this the operator sees a permanent amber bar on every page telling
  // it to prove it is a company — to itself.
  const isPaidPlanRequiringWebsite = ['premium', 'enterprise'].includes(
    (user?.plan_id || 'free').toLowerCase()
  ) && user?.role !== 'admin';
  const currentWebsite = user?.company_website || '';

  useEffect(() => {
    if (!websiteEditing) setWebsiteInput(currentWebsite);
  }, [currentWebsite, websiteEditing]);

  const handleSaveWebsite = async () => {
    const value = websiteInput.trim();
    setWebsiteError('');
    // Premium/Enterprise: enforce non-empty + valid URL
    if (isPaidPlanRequiringWebsite && !value) {
      setWebsiteError(
        language === 'ar'
          ? 'موقع الشركة مطلوب لخطط Premium و Enterprise.'
          : 'A company website is required for Premium and Enterprise plans.'
      );
      return;
    }
    if (value && !WEBSITE_URL_REGEX.test(value)) {
      setWebsiteError(
        language === 'ar'
          ? 'صيغة غير صحيحة. مثال: https://example.com'
          : 'Invalid format. Example: https://example.com (or example.com / www.example.com)'
      );
      return;
    }
    setWebsiteSaving(true);
    try {
      const body = value
        ? { company_website: value, no_company_website: false }
        : { no_company_website: true };
      const response = await fetch(`${BACKEND_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to update website');
      }
      // Update local user state with the returned values
      const updated = { ...user, ...data.user };
      updateUser(updated);
      toast.success(language === 'ar' ? 'تم تحديث ملف الشركة' : 'Company profile updated');
      setWebsiteEditing(false);
    } catch (err) {
      setWebsiteError(err.message || 'Failed to save');
      toast.error(err.message || (language === 'ar' ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setWebsiteSaving(false);
    }
  };

  // Set active tab from URL parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'billing', 'plans', 'settings'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Load subscription status from backend
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/payments/subscription-status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setSubscriptionInfo(data))
      .catch(() => {});
  }, [token]);

  // Load notification preferences from the notification_preferences collection —
  // the SAME store the alert fan-out AND the daily digest job actually read
  // (GET/PUT /api/alerts/preferences). The old /api/auth/notifications store was
  // a dead end that changed nothing.
  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/alerts/preferences`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setNotifPrefs({
          email_enabled: data.email_enabled !== false,
          push_enabled: data.push_enabled !== false,
          in_app_enabled: data.in_app_enabled !== false,
          digest_email_enabled: data.digest_email_enabled !== false,
          digest_time: data.digest_time || '09:00',
          timezone: data.timezone || null,
          team_alerts_enabled: !!data.team_alerts_enabled,
        });
      })
      .catch(() => {});
    // team_alerts row only matters for someone actually in a team
    fetch(`${BACKEND_URL}/api/groups/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setHasTeam(!!(data && data.group)))
      .catch(() => {});
    // WO-21 — Enterprise seats. A 403 here just means "not on this
    // subscription", which is not an error worth showing anybody.
    fetch(`${BACKEND_URL}/api/payments/enterprise/seats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setEntSeats(data))
      .catch(() => {});
  }, [token]);

  const refreshEnterpriseSeats = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/payments/enterprise/seats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEntSeats(r.ok ? await r.json() : null);
    } catch (e) { /* the block simply stays as it was */ }
  };

  // WO-21 — the Enterprise Admin fills and frees their own seats. Every "please
  // add my new hire" used to have to come to MID2BID, because both seat
  // endpoints sit behind the platform-admin guard.
  const handleSeatChange = async (targetUserId, action) => {
    setEntSeatBusy(targetUserId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payments/enterprise/seats/${targetUserId}`,
        { method: action === 'give' ? 'POST' : 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || 'Could not change the seat');
      toast.success(data.message || (language === 'ar' ? 'تم تحديث المقاعد' : 'Seats updated'));
      if (action === 'give') setEntSeatTo('');
      await refreshEnterpriseSeats();
    } catch (err) {
      toast.error(err.message || (language === 'ar' ? 'فشل تحديث المقعد' : 'Seat change failed'));
    } finally {
      setEntSeatBusy(null);
    }
  };

  // WO-21 — hand the Enterprise Admin role to another seat holder. One Admin
  // per company: this replaces the holder, it never adds a second.
  const handleTransferEnterpriseAdmin = async () => {
    if (!entTransferTo) return;
    setEntTransferring(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/enterprise/transfer-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ new_admin_user_id: entTransferTo }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || 'Transfer failed');
      toast.success(data.message || (language === 'ar'
        ? 'تم نقل دور مسؤول الاشتراك.'
        : 'Enterprise Admin transferred.'));
      setEntTransferTo('');
      await refreshEnterpriseSeats();
    } catch (err) {
      toast.error(err.message || (language === 'ar' ? 'فشل النقل' : 'Transfer failed'));
    } finally {
      setEntTransferring(false);
    }
  };

  // Save the WHOLE preferences object (PUT expects every field). Optimistic + revert.
  const saveNotif = async (patch) => {
    const prev = notifPrefs;
    const next = { ...notifPrefs, ...patch };
    setNotifPrefs(next);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/alerts/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(next),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'failed');
      }
      toast.success(language === 'ar' ? 'تم تحديث التفضيلات' : 'Preferences updated');
    } catch (e) {
      setNotifPrefs(prev); // revert on failure
      const msg = e.message && e.message !== 'failed'
        ? e.message
        : (language === 'ar' ? 'فشل تحديث التفضيلات' : 'Failed to update preferences');
      toast.error(msg);
    }
  };

  const TZ_BY_COUNTRY = { SA: 'Asia/Riyadh', AE: 'Asia/Dubai', QA: 'Asia/Qatar', BH: 'Asia/Bahrain', KW: 'Asia/Kuwait', OM: 'Asia/Muscat' };
  const fallbackTz = TZ_BY_COUNTRY[entitlements?.preferred_country] || 'Asia/Riyadh';
  const tzOptions = ['Asia/Riyadh', 'Asia/Dubai', 'Asia/Qatar', 'Asia/Bahrain', 'Asia/Kuwait', 'Asia/Muscat', 'Africa/Cairo', 'Asia/Karachi', 'Asia/Kolkata', 'Europe/London', 'Europe/Madrid', 'America/New_York'];

  const currentPlanId = user?.plan_id?.toLowerCase() || 'free';

  const handlePlanSelect = (plan) => {
    if (plan.id === currentPlanId) return;
    if (plan.id === 'free') {
      // Downgrading to free — show cancel confirmation
      setSelectedPlan(plan);
      setShowConfirmDialog(true);
    } else {
      // Upgrading to paid — go through Stripe Checkout
      handleStripeCheckout(plan);
    }
  };

  const handleStripeCheckout = async (plan) => {
    // WO-44 — billing country, company or individual, and company details first.
    const billing = await askBillingDetails('plan');
    if (!billing) return;
    setIsCheckoutLoading(true);
    try {
      const qty = plan.id === 'enterprise' ? Math.max(5, seatCount) : 1;
      const res = await fetch(`${BACKEND_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
          quantity: qty,
          origin_url: window.location.origin,
          billing,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      toast.error(language === 'ar' ? `فشل في إنشاء جلسة الدفع: ${err.message}` : `Checkout error: ${err.message}`);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/cancel-subscription`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cancellation failed');
      // WO-14 item 7 — cancellation now takes effect at the END of the paid
      // period (WO-13). The plan stays ACTIVE until then; reflect the pending
      // state, do not drop to Free in the UI.
      setSubscriptionInfo(prev => prev ? { ...prev, subscription_status: 'cancelling' } : prev);
      toast.success(language === 'ar'
        ? 'سيتم إلغاء اشتراكك في نهاية فترة الفوترة الحالية'
        : 'Your subscription will be cancelled at the end of the billing period');
      setShowCancelDialog(false);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل إلغاء الاشتراك' : `Failed to cancel: ${err.message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    setIsCancelling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/resume-subscription`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Resume failed');
      // WO-18 B3 — the cancellation is undone; the plan simply continues.
      setSubscriptionInfo(prev => prev ? { ...prev, subscription_status: 'active' } : prev);
      toast.success(language === 'ar'
        ? 'تم التراجع عن الإلغاء — سيستمر اشتراكك'
        : 'Cancellation undone — your subscription will continue');
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل استئناف الاشتراك' : `Failed to resume: ${err.message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!selectedPlan) return;
    setIsChangingPlan(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/update-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ plan_id: selectedPlan.id })
      });

      if (response.ok) {
        if (updateUser) updateUser({ ...user, plan_id: selectedPlan.id });
        toast.success(
          language === 'ar'
            ? `تم تغيير خطتك إلى ${selectedPlan.nameAr}`
            : `Your plan has been changed to ${selectedPlan.name}`
        );
      } else {
        throw new Error('Failed to update plan');
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      toast.error(language === 'ar' ? 'فشل في تغيير الخطة.' : 'Failed to change plan. Please try again.');
    } finally {
      setIsChangingPlan(false);
      setShowConfirmDialog(false);
      setSelectedPlan(null);
    }
  };

  const txt = {
    en: {
      pageTitle: 'Account Settings',
      pageSubtitle: 'Manage your account information and preferences',
      profile: 'Profile',
      billing: 'Billing',
      plans: 'Plans',
      settings: 'Settings',
      profileSection: 'Profile Information',
      profileDesc: 'Your account details as registered',
      name: 'Full Name',
      email: 'Email Address',
      company: 'Company',
      role: 'Account Role',
      memberSince: 'Member Since',
      lastLogin: 'Last Login',
      subscriptionSection: 'Current Subscription',
      subscriptionDesc: 'Your current plan details',
      currentPlan: 'Current Plan',
      planStatus: 'Status',
      active: 'Active',
      features: 'Plan Features',
      contactSupport: 'Contact Support',
      billingSection: 'Billing Information',
      billingDesc: 'Your billing details and history',
      nextBilling: 'Next Billing Date',
      paymentMethod: 'Payment Method',
      billingHistory: 'Billing History',
      noBillingHistory: 'No billing history available',
      invoice: 'Invoice',
      date: 'Date',
      amount: 'Amount',
      status: 'Status',
      paid: 'Paid',
      plansSection: 'Subscription Plans',
      plansDesc: 'Choose the plan that best fits your needs',
      currentPlanBadge: 'Current Plan',
      popular: 'Most Popular',
      selectPlan: 'Select Plan',
      currentlyOn: 'Currently on this plan',
      perMonth: '/month',
      forever: 'forever',
      includedFeatures: 'Included features:',
      changePlanTitle: 'Change Subscription Plan',
      changePlanDesc: 'Are you sure you want to change your plan?',
      changingTo: 'Changing to:',
      cancel: 'Cancel',
      confirm: 'Confirm Change',
      changing: 'Changing...',
      settingsSection: 'Preferences',
      settingsDesc: 'Customize your experience',
      notifications: 'Notifications',
      notificationsDesc: 'Manage email and push notifications',
      emailNotifications: 'Email Notifications',
      pushNotifications: 'Push Notifications',
      languageSettings: 'Language',
      languageDesc: 'Choose your preferred language',
      currentLanguage: 'Current Language',
      english: 'English',
      arabic: 'Arabic'
    },
    ar: {
      pageTitle: 'إعدادات الحساب',
      pageSubtitle: 'إدارة معلومات حسابك وتفضيلاتك',
      profile: 'الملف الشخصي',
      billing: 'الفوترة',
      plans: 'الخطط',
      settings: 'الإعدادات',
      profileSection: 'معلومات الملف الشخصي',
      profileDesc: 'تفاصيل حسابك كما هي مسجلة',
      name: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      company: 'الشركة',
      role: 'دور الحساب',
      memberSince: 'عضو منذ',
      lastLogin: 'آخر تسجيل دخول',
      subscriptionSection: 'الاشتراك الحالي',
      subscriptionDesc: 'تفاصيل خطتك الحالية',
      currentPlan: 'الخطة الحالية',
      planStatus: 'الحالة',
      active: 'نشط',
      features: 'ميزات الخطة',
      contactSupport: 'اتصل بالدعم',
      billingSection: 'معلومات الفوترة',
      billingDesc: 'تفاصيل الفوترة وسجلها',
      nextBilling: 'تاريخ الفوترة التالي',
      paymentMethod: 'طريقة الدفع',
      billingHistory: 'سجل الفوترة',
      noBillingHistory: 'لا يوجد سجل فوترة متاح',
      invoice: 'فاتورة',
      date: 'التاريخ',
      amount: 'المبلغ',
      status: 'الحالة',
      paid: 'مدفوع',
      plansSection: 'خطط الاشتراك',
      plansDesc: 'اختر الخطة التي تناسب احتياجاتك',
      currentPlanBadge: 'الخطة الحالية',
      popular: 'الأكثر شعبية',
      selectPlan: 'اختر الخطة',
      currentlyOn: 'أنت على هذه الخطة حالياً',
      perMonth: '/شهر',
      forever: 'للأبد',
      includedFeatures: 'الميزات المضمنة:',
      changePlanTitle: 'تغيير خطة الاشتراك',
      changePlanDesc: 'هل أنت متأكد أنك تريد تغيير خطتك؟',
      changingTo: 'التغيير إلى:',
      cancel: 'إلغاء',
      confirm: 'تأكيد التغيير',
      changing: 'جارٍ التغيير...',
      settingsSection: 'التفضيلات',
      settingsDesc: 'تخصيص تجربتك',
      notifications: 'الإشعارات',
      notificationsDesc: 'إدارة إشعارات البريد الإلكتروني والدفع',
      emailNotifications: 'إشعارات البريد الإلكتروني',
      pushNotifications: 'إشعارات الدفع',
      languageSettings: 'اللغة',
      languageDesc: 'اختر لغتك المفضلة',
      currentLanguage: 'اللغة الحالية',
      english: 'الإنجليزية',
      arabic: 'العربية'
    }
  };

  const t = txt[language] || txt.en;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentPlan = PLANS.find(p => p.id === currentPlanId) || PLANS[0];

  // Plan Card Component
  const PlanCard = ({ plan, billingCycle }) => {
    const isCurrentPlan = plan.id === currentPlanId;
    const PlanIcon = plan.icon;
    const isYearly = billingCycle === 'yearly' && plan.price > 0 && plan.yearlyPrice;
    const effectivePrice = isYearly ? plan.yearlyPrice : plan.price;
    const yearlyTotal = isYearly ? plan.yearlyPrice * 12 : 0;
    const discountPct = isYearly ? Math.round((1 - plan.yearlyPrice / plan.price) * 100) : 0;
    
    return (
      <Card 
        className={`relative border-2 transition-all duration-300 ${
          isCurrentPlan 
            ? 'border-primary bg-primary/5' 
            : 'border-border hover:border-primary/50 hover:shadow-lg'
        } ${plan.popular ? 'ring-2 ring-primary ring-offset-2' : ''}`}
      >
        {/* Popular Badge */}
        {plan.popular && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-primary text-primary-foreground px-3 py-1">
              {t.popular}
            </Badge>
          </div>
        )}
        
        {/* Current Plan Badge */}
        {isCurrentPlan && (
          <div className="absolute -top-3 right-4">
            <Badge variant="outline" className="border-accent text-accent bg-background px-3 py-1">
              <Check className="w-3 h-3 mr-1" />
              {t.currentPlanBadge}
            </Badge>
          </div>
        )}

        <CardContent className="p-6 pt-8">
          {/* Header */}
          <div className={`flex items-center gap-4 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${plan.color} shadow-lg`}>
              <PlanIcon className="w-6 h-6 text-white" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="text-xl font-bold">{language === 'ar' ? plan.nameAr : plan.name}</h3>
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? plan.descriptionAr : plan.description}
              </p>
            </div>
          </div>

          {/* Price */}
          <div className={`mb-6 ${isRTL ? 'text-right' : ''}`}>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black">${effectivePrice}</span>
              <span className="text-muted-foreground">
                {plan.price === 0
                  ? (language === 'ar' ? plan.periodAr : plan.period)
                  : (language === 'ar' ? '/شهر' : '/month')}
              </span>
              {isYearly && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-semibold ml-1">
                  -{discountPct}%
                </Badge>
              )}
            </div>
            {isYearly && (
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'ar' ? `يُدفع $${yearlyTotal} سنويًا` : `Billed $${yearlyTotal}/yr`}
              </p>
            )}
            {plan.price > 0 && (
              <p className="text-xs text-muted-foreground mt-1" data-testid={`plan-excl-vat-${plan.id}`}>
                {language === 'ar' ? 'غير شامل ضريبة القيمة المضافة' : 'excl. VAT'}
              </p>
            )}
            {!isYearly && plan.yearlyPrice && (
              <p className="text-xs text-emerald-600 font-medium mt-1 cursor-pointer hover:underline" onClick={() => setBillingCycle('yearly')}>
                {language === 'ar'
                  ? `وفّر ${Math.round((1 - plan.yearlyPrice / plan.price) * 100)}% مع الاشتراك السنوي`
                  : `Save ${Math.round((1 - plan.yearlyPrice / plan.price) * 100)}% with yearly billing`}
              </p>
            )}
          </div>

          <Separator className="my-4" />

          {/* Features */}
          <div className={`space-y-3 mb-6 ${isRTL ? 'text-right' : ''}`}>
            <p className="text-sm font-medium text-muted-foreground">{t.includedFeatures}</p>
            {plan.features.map((feature, idx) => (
              <div key={idx} className={`flex items-center gap-2 ${feature.comingSoon ? 'opacity-60' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
                {feature.comingSoon ? (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-600 bg-amber-500/10 shrink-0" data-testid="feature-coming-soon">
                    {language === 'ar' ? 'قريبًا' : 'Soon'}
                  </span>
                ) : (
                  <Check className="w-4 h-4 text-accent shrink-0" />
                )}
                <span className={`text-sm ${feature.comingSoon ? 'text-muted-foreground' : ''}`}>{language === 'ar' ? feature.ar : feature.en}</span>
              </div>
            ))}
            {plan.limitations.map((limit, idx) => (
              <div key={`limit-${idx}`} className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                <X className="w-4 h-4 shrink-0" />
                <span className="text-sm">{language === 'ar' ? limit.ar : limit.en}</span>
              </div>
            ))}
          </div>

          {/* Enterprise: Seat count input */}
          {plan.id === 'enterprise' && !isCurrentPlan && (
            <div className="mb-4">
              <label className="text-xs text-muted-foreground mb-1 block">
                {language === 'ar' ? 'عدد المقاعد (الحد الأدنى 5)' : 'Number of seats (min. 5)'}
              </label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setSeatCount(c => Math.max(5, c - 1))}
                  data-testid="seat-count-decrease"
                >-</Button>
                <span className="text-lg font-bold w-8 text-center">{seatCount}</span>
                <Button
                  variant="outline" size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setSeatCount(c => c + 1)}
                  data-testid="seat-count-increase"
                >+</Button>
                <span className="text-xs text-muted-foreground">
                  = ${(effectivePrice * seatCount).toFixed(0)}/
                  {billingCycle === 'yearly' ? 'yr' : 'mo'}
                </span>
              </div>
            </div>
          )}

          {/* Action Button */}
          <Button 
            className={`w-full ${isCurrentPlan ? 'bg-muted text-muted-foreground' : `bg-gradient-to-r ${plan.color} hover:opacity-90`}`}
            disabled={isCurrentPlan || isCheckoutLoading}
            onClick={() => handlePlanSelect({ ...plan, billingCycle, effectivePrice, yearlyTotal })}
            data-testid={`select-plan-${plan.id}`}
          >
            {isCheckoutLoading && !isCurrentPlan ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {language === 'ar' ? 'جارٍ التحضير...' : 'Redirecting...'}</>
            ) : isCurrentPlan ? t.currentlyOn : (
              plan.id === 'free' ? (language === 'ar' ? 'الخطة المجانية' : 'Downgrade to Free') : (
                <><CreditCard className="w-4 h-4 mr-2" />{t.selectPlan}</>
              )
            )}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-6 max-w-5xl mx-auto ${isRTL ? 'font-arabic' : ''}`} data-testid="settings-page">
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Page Header */}
      <div className={isRTL ? 'text-right' : ''}>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-slate-500 to-zinc-600 shadow-lg">
            <User className="w-7 h-7 text-white" />
          </div>
          {t.pageTitle}
        </h1>
        <p className="text-muted-foreground mt-2">{t.pageSubtitle}</p>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="tab-profile">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">{t.profile}</span>
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2" data-testid="tab-plans">
            <Crown className="w-4 h-4" />
            <span className="hidden sm:inline">{t.plans}</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2" data-testid="tab-billing">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">{t.billing}</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">{t.settings}</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className={isRTL ? 'text-right' : ''}>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                {t.profileSection}
              </CardTitle>
              <CardDescription>{t.profileDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t.name}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg font-medium">
                    {user?.name || '-'}
                  </div>
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t.email}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg font-medium">
                    {user?.email || '-'}
                  </div>
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t.company}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg font-medium">
                    {user?.company_name || '-'}
                  </div>
                </div>

                {/* Company Website (editable) */}
                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`} data-testid="company-website-section">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {language === 'ar' ? 'موقع الشركة' : 'Company Website'}
                    {isPaidPlanRequiringWebsite && (
                      <span className="text-destructive ml-1" title={language === 'ar' ? 'مطلوب لخطة Premium/Enterprise' : 'Required for Premium/Enterprise'}>*</span>
                    )}
                  </label>
                  {websiteEditing ? (
                    <div className="space-y-2">
                      <div className="relative">
                        <Globe className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} w-4 h-4 text-muted-foreground`} />
                        <Input
                          type="text"
                          value={websiteInput}
                          onChange={(e) => { setWebsiteInput(e.target.value); setWebsiteError(''); }}
                          placeholder="https://example.com"
                          className={`${isRTL ? 'pr-9' : 'pl-9'} h-11`}
                          dir="ltr"
                          data-testid="company-website-input"
                          disabled={websiteSaving}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar'
                          ? 'الصيغة: https://example.com (أو example.com / www.example.com)'
                          : 'Format: https://example.com (or example.com / www.example.com)'}
                      </p>
                      {websiteError && (
                        <p className="text-xs text-destructive flex items-center gap-1" data-testid="company-website-error">
                          <AlertCircle className="w-3 h-3" />{websiteError}
                        </p>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveWebsite}
                          disabled={websiteSaving}
                          data-testid="save-website-btn"
                        >
                          {websiteSaving
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Save className="w-4 h-4 mr-1" />}
                          {language === 'ar' ? 'حفظ' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setWebsiteEditing(false); setWebsiteInput(currentWebsite); setWebsiteError(''); }}
                          disabled={websiteSaving}
                          data-testid="cancel-website-btn"
                        >
                          {language === 'ar' ? 'إلغاء' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div
                        className={`p-3 rounded-lg font-medium flex-1 ${
                          isPaidPlanRequiringWebsite && !currentWebsite
                            ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200'
                            : 'bg-muted/50'
                        }`}
                        data-testid="company-website-display"
                      >
                        {currentWebsite ? (
                          <a
                            href={currentWebsite.startsWith('http') ? currentWebsite : `https://${currentWebsite}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline break-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {currentWebsite}
                          </a>
                        ) : isPaidPlanRequiringWebsite ? (
                          <span className="flex items-center gap-1 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {language === 'ar'
                              ? 'مطلوب لخطتك الحالية — يرجى الإضافة'
                              : 'Required for your current plan — please add it'}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            {language === 'ar' ? 'لم يتم التحديد' : 'Not set'}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setWebsiteEditing(true)}
                        data-testid="edit-website-btn"
                      >
                        {language === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {t.role}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    {/* WO-21 — this printed the raw stored value ("admin" /
                        "member"). "member" reads as "member of a team", which
                        is a different thing in this app, and "admin" does not
                        say whose administrator. */}
                    <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
                      {user?.role === 'admin'
                        ? (language === 'ar' ? 'مسؤول MID2BID' : 'MID2BID Admin')
                        : (language === 'ar' ? 'مستخدم' : 'User')}
                    </Badge>
                  </div>
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t.memberSince}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg font-medium">
                    {formatDate(user?.created_at)}
                  </div>
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t.lastLogin}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg font-medium">
                    {user?.last_login_at ? formatDate(user.last_login_at) : (language === 'ar' ? 'غير متاح' : 'Not available')}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Subscription Summary */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className={isRTL ? 'text-right' : ''}>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                {t.subscriptionSection}
              </CardTitle>
              <CardDescription>{t.subscriptionDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`flex items-center justify-between p-4 rounded-lg bg-gradient-to-r ${currentPlan.color} bg-opacity-10 border ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${currentPlan.color} shadow-lg`}>
                    <currentPlan.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <h3 className="font-bold text-lg">{language === 'ar' ? currentPlan.nameAr : currentPlan.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      ${currentPlan.price}{currentPlan.price > 0 ? (language === 'ar' ? t.perMonth : '/month') : ` ${language === 'ar' ? 'للأبد' : 'forever'}`}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setActiveTab('plans')}
                  data-testid="view-plans-btn"
                >
                  {language === 'ar' ? 'عرض الخطط' : 'View Plans'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          <div className={isRTL ? 'text-right' : ''}>
            <h2 className="text-2xl font-bold mb-2">{t.plansSection}</h2>
            <p className="text-muted-foreground">{t.plansDesc}</p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 py-2">
            <span className={`text-sm font-medium transition-colors ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              {language === 'ar' ? 'شهري' : 'Monthly'}
            </span>
            <Switch
              checked={billingCycle === 'yearly'}
              onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
              data-testid="billing-cycle-toggle"
            />
            <span className={`text-sm font-medium flex items-center gap-2 transition-colors ${billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              {language === 'ar' ? 'سنوي' : 'Yearly'}
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-semibold">
                {language === 'ar' ? 'وفّر حتى 18%' : 'Save up to 18%'}
              </Badge>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} billingCycle={billingCycle} />
            ))}
          </div>
          {/* WO-43 — prices are shown without VAT; Stripe adds it at checkout by country. */}
          <p className="text-xs text-muted-foreground text-center" data-testid="plans-vat-note">
            {language === 'ar'
              ? 'الأسعار لا تشمل ضريبة القيمة المضافة. تُضاف الضريبة عند الدفع حيث تنطبق.'
              : 'Prices exclude VAT. VAT is added at checkout where applicable.'}
          </p>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className={isRTL ? 'text-right' : ''}>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                {t.billingSection}
              </CardTitle>
              <CardDescription>{t.billingDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    {t.currentPlan}
                  </label>
                  <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold capitalize">
                        {language === 'ar' ? currentPlan.nameAr : currentPlan.name}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          subscriptionInfo?.subscription_status === 'cancelled'
                            ? 'border-destructive text-destructive'
                            : subscriptionInfo?.subscription_status === 'cancelling'
                            ? 'border-amber-500 text-amber-600'
                            : subscriptionInfo?.subscription_status === 'active'
                            ? 'border-emerald-500 text-emerald-600'
                            : 'border-accent text-accent'
                        }
                      >
                        {subscriptionInfo?.subscription_status === 'cancelled'
                          ? (language === 'ar' ? 'ملغى' : 'Cancelled')
                          : subscriptionInfo?.subscription_status === 'cancelling'
                          ? (language === 'ar' ? 'قيد الإلغاء' : 'Cancelling')
                          : subscriptionInfo?.subscription_status === 'active'
                          ? (language === 'ar' ? 'نشط' : 'Active')
                          : t.active}
                      </Badge>
                    </div>
                    {subscriptionInfo?.subscription_seats && currentPlanId === 'enterprise' && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {subscriptionInfo.subscription_seats} {language === 'ar' ? 'مقعد' : 'seats'}
                      </p>
                    )}
                  </div>
                </div>

                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t.nextBilling}
                  </label>
                  <div className="p-4 bg-muted/50 rounded-lg font-medium">
                    {subscriptionInfo?.subscription_expires_at
                      ? new Date(subscriptionInfo.subscription_expires_at).toLocaleDateString(
                          language === 'ar' ? 'ar-SA' : 'en-US',
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        )
                      : (language === 'ar' ? 'غير متاح' : 'Not available')}
                  </div>
                </div>
              </div>

              {/* WO-14 item 7 — pending cancellation (plan stays active until the
                  API-provided period end; date read from the API, not computed). */}
              {subscriptionInfo?.subscription_status === 'cancelling' && (
                <div className="p-4 border border-amber-500/30 rounded-lg bg-amber-500/10"
                     data-testid="cancellation-pending-banner">
                  <p className="font-medium text-sm text-amber-700 dark:text-amber-400">
                    {language === 'ar' ? 'الإلغاء قيد الانتظار' : 'Cancellation pending'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="cancellation-pending-date">
                    {subscriptionInfo?.subscription_expires_at
                      ? (language === 'ar'
                          ? `تبقى خطتك نشطة حتى ${new Date(subscriptionInfo.subscription_expires_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}، وبعدها ينتهي وصولك.`
                          : `Your plan stays active until ${new Date(subscriptionInfo.subscription_expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}, when your access ends.`)
                      : (language === 'ar'
                          ? 'تبقى خطتك نشطة حتى نهاية فترة الفوترة الحالية.'
                          : 'Your plan stays active until the end of the current billing period.')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-amber-500 text-amber-700 dark:text-amber-400"
                    disabled={isCancelling}
                    onClick={handleResumeSubscription}
                    data-testid="resume-subscription-button"
                  >
                    {language === 'ar' ? 'الاحتفاظ باشتراكي' : 'Keep my subscription'}
                  </Button>
                </div>
              )}

              {/* Cancel Subscription */}
              {/* WO-68 — a complimentary Enterprise has no subscription to cancel. */}
              {currentPlanId !== 'free' && subscriptionInfo?.subscription_status !== 'cancelling'
                && !(currentPlanId === 'enterprise' && entSeats?.complimentary) && (
                <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
                  <div className={`flex items-center justify-between flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={isRTL ? 'text-right' : ''}>
                      <p className="font-medium text-sm">
                        {language === 'ar' ? 'إلغاء الاشتراك' : 'Cancel Subscription'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar'
                          ? 'يبقى وصولك نشطًا حتى نهاية فترة الفوترة الحالية'
                          : 'You keep access until the end of your current billing period'}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowCancelDialog(true)}
                      data-testid="cancel-subscription-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {language === 'ar' ? 'إلغاء' : 'Cancel Plan'}
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Billing History */}
              <div className={isRTL ? 'text-right' : ''}>
                <h4 className="font-semibold mb-4">{t.billingHistory}</h4>
                {subscriptionInfo?.transactions?.length > 0 ? (
                  <div className="space-y-2">
                    {subscriptionInfo.transactions.map((tx, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border text-sm"
                        data-testid={`transaction-row-${idx}`}
                      >
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div>
                            <p className="font-medium capitalize">
                              {tx.payment_type === 'subscription'
                                ? `${tx.plan_id} — ${tx.billing_cycle}`
                                : `Report Purchase`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span className="font-bold">${tx.amount?.toFixed(2)}</span>
                          <Badge
                            className={tx.payment_status === 'paid'
                              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
                              : 'bg-muted text-muted-foreground'}
                          >
                            {tx.payment_status === 'paid'
                              ? (language === 'ar' ? 'مدفوع' : 'Paid')
                              : (language === 'ar' ? 'معلق' : 'Pending')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground border rounded-lg">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t.noBillingHistory}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* WO-21 — Enterprise: the seats, who administers the subscription,
              and the hand-over. Shown only to people actually on an active
              Enterprise subscription. */}
          {entSeats?.active && (
            <Card className="border-0 bg-card/50 backdrop-blur-sm" data-testid="enterprise-admin-card">
              <CardHeader className={isRTL ? 'text-right' : ''}>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  {language === 'ar' ? 'مسؤول الاشتراك المؤسسي' : 'Enterprise Admin'}
                </CardTitle>
                <CardDescription>
                  {language === 'ar'
                    ? 'يدير مسؤول واحد اشتراك الشركة المؤسسي: يوزّع المقاعد ويمكنه إلغاء الاشتراك. لا يرى سجلات CRM الخاصة بزملائه إلا إذا كانوا في الفريق نفسه. للشركة مسؤول واحد فقط.'
                    : 'One person administers your company\u2019s Enterprise subscription: they give out the seats and can cancel it. They see colleagues\u2019 CRM records only if they are in the same Team, like everyone else. A company has only one Enterprise Admin.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                  <label className="text-sm font-medium text-muted-foreground">
                    {language === 'ar' ? 'المقاعد المستخدمة' : 'Seats in use'}
                  </label>
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    {entSeats.assigned_seats} / {entSeats.purchased_seats}
                  </div>
                </div>

                <div className="space-y-2">
                  {entSeats.seats.map((seat) => (
                    <div
                      key={seat.user_id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isRTL ? 'flex-row-reverse' : ''}`}
                      data-testid={`enterprise-seat-${seat.user_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                          {seat.name || seat.email}
                          {seat.is_you && (
                            <Badge variant="outline" className="text-[10px]">
                              {language === 'ar' ? 'أنت' : 'You'}
                            </Badge>
                          )}
                          {seat.is_enterprise_admin && (
                            <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] gap-1">
                              <Shield className="w-3 h-3" />
                              {language === 'ar' ? 'مسؤول المؤسسة' : 'Enterprise Admin'}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{seat.email}</div>
                      </div>
                      {/* WO-21 — the Admin frees a seat. Never their own: they
                          would stop being Enterprise while still administering
                          the subscription, so the server refuses it too. */}
                      {entSeats.is_enterprise_admin && !seat.is_enterprise_admin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSeatChange(seat.user_id, 'take')}
                          disabled={entSeatBusy === seat.user_id}
                          data-testid={`enterprise-seat-remove-${seat.user_id}`}
                        >
                          {entSeatBusy === seat.user_id
                            ? (language === 'ar' ? '…' : '…')
                            : (language === 'ar' ? 'سحب المقعد' : 'Take seat back')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* WO-21 — fill an empty seat from the company's own people. */}
                {entSeats.is_enterprise_admin && (
                  <div className={`space-y-2 pt-2 border-t ${isRTL ? 'text-right' : ''}`}>
                    <label className="text-sm font-medium text-muted-foreground">
                      {language === 'ar' ? 'منح مقعد إلى' : 'Give a seat to'}
                      {' '}
                      <span className="font-normal">
                        ({Math.max(0, (entSeats.purchased_seats || 0) - (entSeats.assigned_seats || 0))}{' '}
                        {language === 'ar' ? 'مقعد متاح' : 'free'})
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={entSeatTo}
                        onChange={(e) => setEntSeatTo(e.target.value)}
                        className="h-11 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="enterprise-seat-give-select"
                      >
                        <option value="">
                          {language === 'ar' ? 'اختر شخصًا من شركتك…' : 'Choose someone from your company…'}
                        </option>
                        {(entSeats.company_users || [])
                          .filter((cu) => !cu.has_seat && cu.status === 'active')
                          .map((cu) => (
                            <option key={cu.user_id} value={cu.user_id}>
                              {(cu.name || cu.email)} — {cu.email}
                            </option>
                          ))}
                      </select>
                      <Button
                        onClick={() => handleSeatChange(entSeatTo, 'give')}
                        disabled={!entSeatTo || entSeatBusy === entSeatTo
                                  || (entSeats.assigned_seats >= entSeats.purchased_seats)}
                        data-testid="enterprise-seat-give-btn"
                      >
                        {language === 'ar' ? 'منح مقعد' : 'Give seat'}
                      </Button>
                    </div>
                    {entSeats.assigned_seats >= entSeats.purchased_seats && (
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {language === 'ar'
                          ? 'جميع المقاعد مستخدمة. اسحب مقعدًا أو اشترِ مقاعد إضافية.'
                          : 'Every seat is in use. Take one back, or buy more seats.'}
                      </p>
                    )}
                    {(entSeats.company_users || []).some((cu) => !cu.has_seat && cu.status !== 'active') && (
                      <p className="text-xs text-muted-foreground">
                        {language === 'ar'
                          ? 'لا تظهر الحسابات التي لم تُفعّل بعد — يجب تأكيد البريد الإلكتروني أولًا.'
                          : 'People who have not verified their email yet are not listed — they must verify first.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Only the current holder can hand it over, and only to
                    somebody who already holds one of the company's seats. */}
                {entSeats.is_enterprise_admin && entSeats.seats.length > 1 && (
                  <div className={`space-y-2 pt-2 border-t ${isRTL ? 'text-right' : ''}`}>
                    <label className="text-sm font-medium text-muted-foreground">
                      {language === 'ar'
                        ? 'نقل دور مسؤول المؤسسة إلى'
                        : 'Transfer Enterprise Admin to'}
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={entTransferTo}
                        onChange={(e) => setEntTransferTo(e.target.value)}
                        className="h-11 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="enterprise-transfer-select"
                      >
                        <option value="">
                          {language === 'ar' ? 'اختر عضوًا…' : 'Choose a colleague…'}
                        </option>
                        {entSeats.seats
                          .filter((sx) => !sx.is_enterprise_admin && sx.status === 'active')
                          .map((sx) => (
                            <option key={sx.user_id} value={sx.user_id}>
                              {(sx.name || sx.email)} — {sx.email}
                            </option>
                          ))}
                      </select>
                      <Button
                        onClick={handleTransferEnterpriseAdmin}
                        disabled={!entTransferTo || entTransferring}
                        data-testid="enterprise-transfer-btn"
                      >
                        {entTransferring
                          ? (language === 'ar' ? 'جارٍ النقل…' : 'Transferring…')
                          : (language === 'ar' ? 'نقل' : 'Transfer')}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {language === 'ar'
                        ? 'ستفقد إدارة الاشتراك وتبقى مستخدمًا مؤسسيًا. لا يتغير أسلوب الدفع المسجل.'
                        : 'You will stop administering the subscription and stay an ordinary Enterprise user. The payment method on file does not change.'}
                    </p>
                  </div>
                )}

                {/* WO-68 — the company-wide CRM view is gone (only a Team shares a
                    CRM), so the old disclosure note is gone with it. A free
                    Enterprise given by MID2BID says so instead. */}
                {entSeats.complimentary && (
                  <p className="text-xs text-muted-foreground pt-2 border-t" data-testid="enterprise-complimentary-note">
                    {language === 'ar'
                      ? 'اشتراك المؤسسة هذا مقدَّم مجانًا من Mid2Bid: لا يوجد ما تدفعه أو تلغيه.'
                      : 'This Enterprise subscription is complimentary, given by Mid2Bid: there is nothing to pay or cancel.'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className={isRTL ? 'text-right' : ''}>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                {t.notifications}
              </CardTitle>
              <CardDescription>{t.notificationsDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'email_enabled', icon: Mail, label: language === 'ar' ? 'إشعارات البريد الإلكتروني' : 'Email notifications' },
                { key: 'push_enabled', icon: Bell, label: language === 'ar' ? 'إشعارات الدفع' : 'Push notifications' },
                { key: 'in_app_enabled', icon: Bell, label: language === 'ar' ? 'الإشعارات داخل التطبيق' : 'In-app notifications' },
              ].map(({ key, icon: Icon, label }) => (
                <div key={key} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Icon className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">{label}</span>
                  </div>
                  <Switch
                    checked={!!notifPrefs[key]}
                    onCheckedChange={(v) => saveNotif({ [key]: v })}
                    data-testid={`notif-${key}`}
                  />
                </div>
              ))}

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium">{language === 'ar' ? 'أرسل لي الملخص اليومي' : 'Send me the daily digest'}</span>
                </div>
                <Switch
                  checked={!!notifPrefs.digest_email_enabled}
                  onCheckedChange={(v) => saveNotif({ digest_email_enabled: v })}
                  data-testid="notif-digest_email_enabled"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                  <span className="font-medium text-sm block">{language === 'ar' ? 'وقت إرسال الملخص' : 'Daily digest time'}</span>
                  <input
                    type="time"
                    value={notifPrefs.digest_time || '09:00'}
                    onChange={(e) => setNotifPrefs(p => ({ ...p, digest_time: e.target.value }))}
                    onBlur={(e) => saveNotif({ digest_time: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    data-testid="notif-digest-time"
                  />
                </div>
                <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                  <span className="font-medium text-sm block">{language === 'ar' ? 'المنطقة الزمنية' : 'My timezone'}</span>
                  <select
                    value={notifPrefs.timezone || ''}
                    onChange={(e) => saveNotif({ timezone: e.target.value || null })}
                    className="w-full h-10 px-3 rounded-md border bg-background"
                    data-testid="notif-timezone"
                  >
                    <option value="">{language === 'ar' ? `اتبع دولتي (${fallbackTz})` : `Follow my country (${fallbackTz})`}</option>
                    {tzOptions.map(z => <option key={z} value={z}>{z}</option>)}
                    {notifPrefs.timezone && !tzOptions.includes(notifPrefs.timezone) && (
                      <option value={notifPrefs.timezone}>{notifPrefs.timezone}</option>
                    )}
                  </select>
                </div>
              </div>

              {hasTeam && (
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Bell className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">{language === 'ar' ? 'أبلغني أيضًا عن التنبيهات التي أنشأها فريقي' : 'Also notify me about alerts created by my team'}</span>
                  </div>
                  <Switch
                    checked={!!notifPrefs.team_alerts_enabled}
                    onCheckedChange={(v) => saveNotif({ team_alerts_enabled: v })}
                    data-testid="notif-team_alerts_enabled"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className={isRTL ? 'text-right' : ''}>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                {t.languageSettings}
              </CardTitle>
              <CardDescription>{t.languageDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                <label className="text-sm font-medium text-muted-foreground">
                  {t.currentLanguage}
                </label>
                <div className="p-4 bg-muted/50 rounded-lg font-medium">
                  {language === 'en' ? t.english : t.arabic}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {language === 'en' 
                    ? 'Use the language toggle in the header to switch languages' 
                    : 'استخدم زر تبديل اللغة في الرأس لتغيير اللغة'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Change Confirmation Dialog */}
      {billingDialog}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent data-testid="change-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t.changePlanTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.changePlanDesc}
              {selectedPlan && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">{t.changingTo}</p>
                  <p className="font-bold text-lg text-foreground">
                    {language === 'ar' ? selectedPlan.nameAr : selectedPlan.name}
                    {' — free forever'}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isChangingPlan} data-testid="cancel-plan-change">
              {t.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPlanChange}
              disabled={isChangingPlan}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-plan-change"
            >
              {isChangingPlan ? t.changing : t.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid="cancel-subscription-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'ar' ? 'إلغاء الاشتراك' : 'Cancel Subscription'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {/* WO-22 — this used to say the plan drops to Free "immediately",
                    which is not what the server does: it cancels at the END of
                    the period and the reply says so in as many words ("You keep
                    full access until then"). Frightening a customer into
                    thinking they lose the month they have already paid for is
                    the wrong way round. */}
                <p>
                  {language === 'ar'
                    ? 'سيستمر اشتراكك حتى نهاية الفترة المدفوعة الحالية، ثم لن يتم تجديده. تحتفظ بجميع الميزات حتى ذلك التاريخ.'
                    : 'Your subscription stays active until the end of the period you have already paid for, and then it will not renew. You keep every paid feature until that date.'}
                </p>
                {/* WO-24 — this said the customer would be REMOVED from their
                    Team and would need a new invitation. WO-23 made that false,
                    and the server's own answer was corrected then — but this
                    screen keeps its own copy of the sentence and never reads the
                    server's, so the false one is what the customer saw. It is
                    also the last thing anybody reads before cancelling, so the
                    true version — nothing deleted, your place kept, back
                    immediately if you subscribe again — is both the honest
                    sentence and the one most likely to keep them. */}
                {hasTeam && (
                  <p className="text-amber-700 dark:text-amber-400" data-testid="cancel-team-warning">
                    {language === 'ar'
                      ? 'عندما تنتهي الفترة ستتوقف عضويتك في الفريق مؤقتًا: لن يرى زملاؤك جهات الاتصال والفرص التي أنشأتها، وسترى ما أنشأته أنت فقط. لا يُحذف أي سجل ولا تفقد مكانك في الفريق — إذا اشتركت من جديد تعود فورًا دون حاجة إلى دعوة جديدة.'
                      : 'When it ends your Team membership will be paused: your colleagues will no longer see the contacts and opportunities you created, and you will only see your own. Nothing is deleted and you keep your place on the Team — subscribe again and you are back immediately, with no new invitation needed.'}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling} data-testid="cancel-cancel-subscription">
              {language === 'ar' ? 'تراجع' : 'Keep my plan'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={isCancelling}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-cancel-subscription"
            >
              {isCancelling
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'ar' ? 'جارٍ الإلغاء...' : 'Cancelling...'}</>
                : (language === 'ar' ? 'نعم، إلغاء' : 'Yes, Cancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
